import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { type TransactionFeesSettings } from '~entities/payment/api/fetch-transaction-fees-settings';
import { useToggleTransactionFees } from '~entities/payment/api/toggle-transaction-fee-settings';

import { MultiParentSelector } from '~features/parents/ui/multi-parent-selector';

import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';

import { DEFAULT_FEES } from '../lib/constants';

export interface ExcludeParentDialogProps {
  transactionFeesSettings: TransactionFeesSettings | undefined;
  isFetching: boolean;
}

export const ExcludeParentDialog = ({
  transactionFeesSettings,
  isFetching,
}: ExcludeParentDialogProps) => {
  const [open, setOpen] = useState(false);
  const [selectedParents, setSelectedParents] = useState<Array<string>>([]);

  const { mutateAsync: toggleTransactionFees, isPending } =
    useToggleTransactionFees();

  const handleSave = async () => {
    try {
      await toggleTransactionFees({
        passTransactionFeesToCustomers:
          transactionFeesSettings?.passTransactionFeesToCustomers ?? false,
        cardTransactionFees:
          transactionFeesSettings?.cardTransactionFees ??
          DEFAULT_FEES.CARD_TRANSACTION_PERCENTAGE,
        cardFixedFee:
          transactionFeesSettings?.cardFixedFee ?? DEFAULT_FEES.CARD_FIXED,
        achTransactionFees:
          transactionFeesSettings?.achTransactionFees ??
          DEFAULT_FEES.ACH_TRANSACTION_PERCENTAGE,
        updateInvoices: transactionFeesSettings?.updateInvoices ?? false,
        excludedParentIds: [
          ...(transactionFeesSettings?.excludedParentIds.map(
            (parent) => parent.parentId,
          ) ?? []),
          ...selectedParents,
        ],
      });
      toast.success('Parent excluded from transaction fees successfully');
      setOpen(false);
      setSelectedParents([]);
    } catch (error) {
      console.error('Error excluding parent from transaction fees:', error);
      toast.error('Failed to exclude parent from transaction fees');
    }
  };

  return (
    <>
      {isFetching ? (
        <div className='flex justify-center py-8'>
          <Loader2 className='animate-spin' />
        </div>
      ) : (
        <>
          <Button
            onClick={() => {
              setOpen(true);
            }}
          >
            Add Parent to Exclusion List
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Manage Excluded Parents</DialogTitle>
                <DialogDescription>
                  Select the parent you want to exclude from transaction fees.
                </DialogDescription>
              </DialogHeader>

              <MultiParentSelector
                onChange={(value) => {
                  setSelectedParents(value);
                }}
                filterOption={(option) => {
                  return !transactionFeesSettings?.excludedParentIds.some(
                    (p) => p.parentId === option.value,
                  );
                }}
                parentOptionsCount={50}
              />

              <DialogFooter>
                <DialogClose asChild>
                  <Button variant='outline'>Cancel</Button>
                </DialogClose>
                <Button onClick={handleSave} disabled={isPending}>
                  {isPending && <Loader2 className='animate-spin' />}
                  Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </>
  );
};
